// actions
import cloneDeep from 'lodash/cloneDeep';

import { visibilityTracker } from '@/modules/public/search/data-tracking/search-visibility-tracker.js';
import {
  FROM_ROOM_TAG_PARAM, parseFromRoomTagSlug, saveEventTypeChipSwitch,
} from '@/modules/public/search/services/event-type-chips';
import { trackSearchVisit } from '@/services/search-interactions-tracker.js';
import { getCachedActivities, setCachedActivities } from '@/utils/managerLastActivityCache.js';
import request from '@/utils/request';
import { getOnlineLabelStatus } from '@/utils/responseTime';

import { setRoomTag } from '../../../../../../modules/room-tag-storage.js';
import { setSearchFilters } from '../../../../../../modules/search-filters-storage';
import {
  enlargeBoundaries,
  generateSearchPath, getUrlParam, scrollTop, stateToQuery, stateToQueryProps, updateURLParams,
} from '../services.js';
import roomActions from './roomActions.js';

// id of active room request
// abort all not latest requests
let roomsRequestId;
let activeRequest;

let timeTrackingWatch;

let searchActivityTimer = null;
let visibilityListenerAdded = false;
let lastVenueIds = [];

const GA4FiltersMap = {
  Event: 'event_tag_slug',
  Location: 'location',
  Date: 'date',
  Time: 'time',
  People: 'people',
  Layout: 'layout',
  Price: 'price',
  'AdvancedFilter-catering': 'catering',
  'AdvancedFilter-roomFeatures': 'room_features',
  'AdvancedFilter-allowedEvents': 'allowed_events',
  'AdvancedFilter-venueFeatures': 'venue_features',
  flexibleCancellation: 'cancellation_flexible',
  venueParts: 'venue_parts',
  noAgeRestriction: 'no_age_restriction',
  superVenuesOnly: 'supervenues',
};

const actions = {
  initTimeTrackingWatchEvents() {
    window.addEventListener('focus', () => {
      timeTrackingWatch && timeTrackingWatch.start();
    });
    window.addEventListener('blur', () => {
      timeTrackingWatch && timeTrackingWatch.stop();
    });
  },
  listenToUrlHistoryChange({ dispatch }) {
    window.addEventListener('popstate', (event) => {
      dispatch('reloadPageFromHistoryState', event.state);
    });
  },
  reloadPageFromHistoryState({ commit, dispatch }, historyState) {
    if (!historyState) {
      window.location.reload();
      return;
    }

    commit('setMobileView', historyState.view);
    if (historyState.view === 'map') {
      $.publish('v.search.init');
    }

    commit('updatePeople', historyState.people);
    commit('updateEventType', {
      key: historyState.eventTypeSlug,
      value: historyState.eventType,
    });
    commit('setFromRoomTagSlug', parseFromRoomTagSlug(getUrlParam(FROM_ROOM_TAG_PARAM)));
    commit('locationNameChange', historyState.neighbourhood);
    commit('onDateChange', historyState.date);
    commit('setMetaHeader', historyState.metaHeader);
    commit('layoutChange', historyState.room_layout);
    commit('setPage', historyState.page);
    commit('timeChange', [historyState.time_from, historyState.time_to]);
    commit('superVenuesOnlyChange', historyState.supervenues_only);
    commit('flexibleCancellationChange', historyState.flexible_cancellation_only);
    commit('priceChange', [historyState.min_price, historyState.max_price]);
    commit('priceMethodChange', historyState.price_method);

    dispatch('applyFilters', {
      changedFilter: 'BackButton',
      value: 'back',
    });
  },
  updateUrlFromState({ state, getters }, { replaceState } = {}) {
    let query = stateToQuery({ state, getters });
    if (getters.activeEventTypeChip) {
      query += `&${$.param({ [FROM_ROOM_TAG_PARAM]: state.fromRoomTagSlug })}`;
    }

    const searchPath = generateSearchPath(state.routes.search, state.l10n.countrySubdir, state.eventTypeSlug);
    const historyApiState = stateToQueryProps({ state, getters });
    historyApiState.eventTypeSlug = state.eventTypeSlug;
    historyApiState.eventType = state.eventType;
    historyApiState.metaHeader = state.metaHeader;

    updateURLParams({
      query,
      historyApiState,
      searchPath,
      replaceState,
    });

    // save event type for data tracking on every change
    window.__DATA_TRACKING_VAR__TAG_SLUG = state.eventTypeSlug;
  },
  highlightMarker(store, venueId) {
    actions.jQueryPublish('v.search.highlightMarker', [venueId]);
  },
  unHighlightMarker(store, venueId) {
    actions.jQueryPublish('v.search.unHighlightMarker', [venueId]);
  },
  jQueryPublish(actionName, data) {
    App.es6.shared.waitFor(() => {
      return window.$ && window.$.publish;
    }, error => {
      if (error) {
        Rollbar.error('$.publish not available after few attempts', { actionName, data, error });
        return;
      }
      $.publish(actionName, data);
    }, { interval: 250, attempts: 4 });
  },
  locationChange({ commit }, address) {
    commit('locationNameChange', address.value);
    $.publish('v.search.locationChange', {
      location: address.location,
    });
  },
  resetMapToInitialLocation({ state }) {
    const location = state.initialLocation;

    $.publish('v.search.locationChange', {
      location,
    });
  },
  enlargeMap({ state }) {
    $.publish('v.search.locationChange', {
      location: enlargeBoundaries(state.boundaries),
    });
  },
  resetMapToInitialLocationWithoutRefresh({ state }) {
    const location = state.initialLocation;

    $.publish('v.search.locationChange', {
      location,
      isMapResetTrigger: true,
    });
  },
  mapBoundariesChanged({ commit, dispatch }, bounds) {
    const newBoundaries = {
      longitude_from: bounds.west,
      longitude_to: bounds.east,
      latitude_from: bounds.south,
      latitude_to: bounds.north,
    };

    commit('setNewBoundaries', newBoundaries);

    let trigger = bounds.initial ? 'initMap' : 'map';
    trigger = bounds.isMapResetTrigger ? 'map-reset' : trigger;
    dispatch('getRooms', { trigger });
    dispatch('scrollTop');
  },
  clearVenueTypes({ state, commit }) {
    if (!state.filters.venueTypes.value.length) {
      return;
    }
    commit('setVenueTypes', []);
  },
  changeEventType({ commit, dispatch }, eventType) {
    dispatch('clearVenueTypes');
    // Picked in the search field, so no event-type chip stands for it any more
    commit('setFromRoomTagSlug', null);
    commit('updateEventType', {
      key: eventType.key,
      value: eventType.value,
    });
  },
  // A full page load, so the new event type comes with its own quick filters, meta tags and filter config
  reloadSearchWithEventType({ state, getters, commit }, { eventTypeSlug, carriedQuickFilters }) {
    // Chip to chip keeps pointing at the search the Client started from, so unselecting still goes all the way back
    const originalSlug = getters.activeEventTypeChip ? state.fromRoomTagSlug : state.eventTypeSlug;
    const query = { ...stateToQueryProps({ state, getters }), room_tag: eventTypeSlug };
    delete query.page;
    if (originalSlug && originalSlug !== eventTypeSlug) {
      query[FROM_ROOM_TAG_PARAM] = originalSlug;
    }

    const searchPath = generateSearchPath(state.routes.search, state.l10n.countrySubdir, eventTypeSlug);
    saveEventTypeChipSwitch(searchPath, carriedQuickFilters);
    commit('setLoading');
    window.location.assign(`${searchPath}?${$.param(query)}`);
  },
  applyFakeLoading({ commit }) {
    commit('setLoading');
    setTimeout(() => {
      commit('setLoaded');
    }, 500);
  },
  applyFilters({ dispatch }, { changedFilter = 'custom', value } = {}) {
    dispatch('getRooms', {
      trigger: changedFilter,
      triggerValue: value,
    });

    if (changedFilter === 'Event') {
      dispatch('updateMetaTags');
    }

    dispatch('clearHighlightedRoom');

    window.scrollTo(0, 0);
  },
  abortAjax({ commit }) {
    roomsRequestId = null;
    if (activeRequest) {
      activeRequest.abort();
    }
    commit('setLoaded');
  },

  getQuickFilters({ commit, getters, state }) {
    const queryState = { ...state };
    const params = stateToQuery(
      { state: queryState, getters },
      false,
      state.debugFilterMode,
    );

    $.ajax({
      url: `/ajax/quick-filters?${params}`,
      dataType: 'json',
    })
      .done((data) => {
        if (Array.isArray(data.quick_filters) && data.quick_filters?.length) {
          commit('setQuickFilters', data.quick_filters);
        }
      })
      .fail((response) => {
        if (response && response.status === 422) {
          Rollbar.error('Search: Failed to get quick filters', {
            filters: state.filters,
          });
        }
      });
  },

  getRooms(
    {
      commit, getters, state, dispatch,
    },
    { trigger, triggerValue } = { trigger: 'custom', triggerValue: null },
  ) {
    console.log('trigger', trigger, triggerValue);
    commit('setTrigger', trigger);
    const requestId = `${Date.now()}${Math.random()}`;
    roomsRequestId = requestId;

    if (activeRequest) {
      activeRequest.abort();
      commit('setLoaded');
    }

    const queryState = { ...state };

    if (trigger !== 'Page') {
      queryState.page = 1;
    }

    const params = stateToQuery(
      { state: queryState, getters },
      trigger === 'initMap',
      state.debugFilterMode,
    );

    if (trigger !== 'initMap') {
      commit('setLoading');
    }

    // TM-3461
    commit('setReducedFilters', []);
    commit('setRoomsReducedFilters', []);
    commit('setLoadingReducedFilters', true);

    dispatch('trackVisibleRooms');

    dispatch('getQuickFilters');

    activeRequest = $.ajax({
      url: `/ajax/search-list?${params}&REAL_SEARCH=true`,
      dataType: 'json',
    })
      .done((data) => {
        // ignore result as there is new request pending
        if (requestId !== roomsRequestId) {
          return;
        }
        activeRequest = null;

        commit('setMetaHeader', data.meta.header);
        commit('setHashId', data.meta.hash);
        commit('setPricingMethodCounter', data.meta.pricing_method_counter);
        commit('setTotalRooms', {
          totalRooms: data.meta.hits_count,
        });

        commit('setLoadedEventType', data.meta?.room_tag);

        // show first only subset of rooms to display them as fast possible
        // load the rest when first rooms are already there
        if (trigger !== 'initMap') {
          commit('setRooms', data.rooms);
          commit('setAdditionalRoomTags', data.meta.additional_room_tags);
          commit('setDisplayedRooms', state.isMobile ? 6 : 12);
          commit('setTotalDisplayedRooms', data.meta.hits_count);
          commit('setEventCategory', data.meta.room_tag_category);

          if (trigger !== 'Page') {
            commit('setPage', 1);
          }

          dispatch('getManagersLastActivityOnline');

          setTimeout(() => {
            commit('setDisplayedRooms', Math.min(data.rooms.length, state.perPage));
            dispatch('observeVisibleRoomsAsync');
          }, 500);
          $.publish('v.search.rooms-loaded');
        } else {
          dispatch('observeVisibleRoomsAsync');
          dispatch('getManagersLastActivityOnline');
        }

        dispatch('getRoomsReducedFilters', { state });

        commit('setLoaded');

        dispatch('updateTrackingCity');

        // do not push new history state for these updates
        const replaceState = trigger === 'initMap'
          || trigger === 'map'
          || trigger.indexOf('AdvancedFilter-') === 0
          || trigger === 'BackButton';
        dispatch('updateUrlFromState', {
          replaceState,
        });
        dispatch('setTotalResultsVariable', state.rooms.length);
        $.publish('v.search.points', {
          data: data.allRoomsPositions,
          trigger,
        });

        dispatch('trackGA4Filters', {
          filter: trigger,
          filterValue: triggerValue,
        });
      })
      .fail((response) => {
        if (response && response.status === 422) {
          Rollbar.error('Search: Failed to get search data', {
            filters: state.filters,
            params,
          });
        }
        activeRequest = null;
        commit('setLoaded');
      });

    dispatch('setRoomTagCookie');
    dispatch('setFiltersCookie');

    if (state.debugFilterMode) {
      dispatch('getDebugFiltersRoom');
    }
  },
  getDebugFiltersRoom({ state, getters, commit }) {
    const queryState = { ...state };
    queryState.page = 1;

    const params = stateToQuery(
      { state: queryState, getters },
      false,
    );

    $.ajax({
      url: `/ajax/search-list?${params}`,
      dataType: 'json',
    })
      .done((data) => {
        const roomIds = data.rooms.map(room => room.room_id);
        commit('setDebugRoomsMap', roomIds);
      });
  },
  enableDebugFilterModeFromDMSToolbar({ commit, dispatch }) {
    commit('enableDebugFilterMode');
    dispatch('getRooms');
  },
  async getRoomsReducedFilters({ getters, dispatch, commit }, { state, loopIndex = 0, removedFilters = [] }) {
    if (state.page > 1 || state.totalRooms === 0 || state.totalRooms > 12 || getters.selectedOptionsCount < 2) {
      commit('setLoadingReducedFilters', false);
      return;
    }

    const queryState = { ...state };
    queryState.filters = cloneDeep(queryState.filters);
    queryState.page = 1;

    const removableFiltersConfig = [
      // Only show cancelation flexible
      { field: 'flexibleCancellation', value: false, label: 'Flexible cancellation policy' },
      // Supervenue
      { field: 'superVenuesOnly', value: false, label: 'Supervenue' },
      // Area type
      { field: 'venueParts', value: [] },
      // Facilities
      { field: 'roomFeatures', keys: ['5', '12', '8', '19', '9', '6', '11', '7', '10', '16', '13'] },
      // Venue type ??
      { field: 'venueTypes', value: [], label: 'Venue type' },
      // Menu ??
      { field: 'catering', key: '20' }, // Vegetarian menu
      { field: 'catering', key: '14' }, // Vegan menu
      { field: 'catering', key: '2' }, // Halal menu
      { field: 'catering', key: '15' }, // Gluten-free menu
      { field: 'catering', key: '3' }, // Kosher menu
      { field: 'catering', key: '21' }, // Free refreshments
      // Alcohol (only for categories except party and wedding)
      { field: 'catering', key: '8', excludeEventCategories: ['party', 'wedding'] }, // Venue provides alcohol
      { field: 'catering', key: '7', excludeEventCategories: ['party', 'wedding'] }, // BYO alcohol allowed
      { field: 'catering', key: '1000', excludeEventCategories: ['party', 'wedding'] }, // No corkage fee for BYO alcohol
      // Catering (only for categories except party and wedding)
      { field: 'catering', value: [], excludeEventCategories: ['party', 'wedding'] },
      // Layout
      { field: 'layout', value: null },
      // Time ??
      { field: 'timeRange', value: null },
      // Date ??
      { field: 'date', value: null },
      // Price method
      { field: 'priceMethod', value: null },
      // Price range
      { field: 'priceRange' },
    ];

    let removedFilter = null;
    for (let i = 0; i < removableFiltersConfig.length; i += 1) {
      const filterConfig = removableFiltersConfig[i];

      if (filterConfig.excludeEventCategories && filterConfig.excludeEventCategories.includes(state.eventCategory)) {
        continue;
      }

      if (filterConfig.field === 'priceRange') {
        if (!getters.isPriceFilterDefault) {
          queryState.filters.priceRange.value.from = queryState.priceBoundaries.default[0];
          queryState.filters.priceRange.value.to = queryState.priceBoundaries.default[1];
          removedFilter = filterConfig;
        }
        loopIndex = 4; // no more iterations
        break; // priceRange is last
      }

      if (filterConfig.field === 'timeRange') {
        if (queryState.filters.timeFrom.value || queryState.filters.timeTo.value) {
          queryState.filters.timeFrom.value = null;
          queryState.filters.timeTo.value = null;
          removedFilter = filterConfig;
          break;
        }
        continue;
      }

      if (filterConfig.keys) {
        const foundKey = filterConfig.keys.find(key => queryState.filters[filterConfig.field].value.includes(key));
        if (foundKey) {
          queryState.filters[filterConfig.field].value = queryState.filters[filterConfig.field].value.filter(key => !filterConfig.keys.includes(key));
          removedFilter = filterConfig;
          break;
        }
        continue;
      }

      if (filterConfig.key) {
        if (queryState.filters[filterConfig.field]?.value?.includes(filterConfig.key)) {
          queryState.filters[filterConfig.field].value = queryState.filters[filterConfig.field].value.filter(key => key !== filterConfig.key);
          removedFilter = filterConfig;
          break;
        }

        continue;
      }

      if (queryState.filters[filterConfig.field]?.value) {
        // e.g. venueParts, venueTypes
        if (typeof queryState.filters[filterConfig.field]?.value === 'object' && queryState.filters[filterConfig.field]?.value.length) {
          queryState.filters[filterConfig.field].value = filterConfig.value;
          removedFilter = filterConfig;
          break;
        }

        if (typeof queryState.filters[filterConfig.field]?.value !== 'object') {
          queryState.filters[filterConfig.field].value = filterConfig.value;
          removedFilter = filterConfig;
          break;
        }
      }
    }

    if (removedFilter === null) {
      commit('setLoadingReducedFilters', false);
      return;
    }
    removedFilters.push(removedFilter);

    let params = stateToQueryProps({ state: queryState, getters }, false);
    params = $.param(params);

    commit('setLoadingReducedFilters', true);
    const data = await request(`/ajax/search-list?${params}`, {
      headers: { 'x-search-reduced-filters': 'true' },
    });
    commit('setLoadingReducedFilters', false);

    // remove duplicates from the list (store.state.rooms)
    const rooms = data.rooms;
    const existingRoomIds = state.rooms.map(room => room.room_id);
    const newRooms = rooms.filter(room => !existingRoomIds.includes(room.room_id));

    if (newRooms.length >= 3) {
      commit('setRoomsReducedFilters', newRooms);
      commit('setReducedFilters', removedFilters);
      commit('setLoadingReducedFilters', false);

      $.publish('v.search.points', {
        data: data.allRoomsPositions,
        trigger: 'reducedFilters',
      });

      dispatch('fetchManagersLastActivityOnline', [...new Set(newRooms.map(room => room.venue_id))]);

      return;
    }

    if (loopIndex > 3) { // max 4 iterations
      commit('setLoadingReducedFilters', false);
      return;
    }

    // try to reduce more filters
    dispatch('getRoomsReducedFilters', { state: queryState, loopIndex: loopIndex + 1, removedFilters });
  },
  showMap({ commit, dispatch }) {
    commit('showMap');
    $.publish('v.search.init');
    dispatch('scrollTop');
    dispatch('updateUrlFromState');
  },
  showFilters({ commit, dispatch }) {
    commit('showFilters');
    dispatch('scrollTop');
    dispatch('updateUrlFromState');
  },
  showResults({ commit, dispatch }) {
    commit('showResults');
    dispatch('scrollTop');
    dispatch('updateUrlFromState');

    // fix issue when window is resized and invisible carousels change slides
    window.setTimeout(() => window.dispatchEvent(new Event('resize')), 10);
  },
  scrollTop() {
    scrollTop();
  },
  focusPeopleInput({ dispatch }) {
    dispatch('scrollTop');
    setTimeout(() => window.document.querySelector('#people').focus(), 10);
  },
  focusDateInput({ dispatch }) {
    dispatch('scrollTop');
    setTimeout(() => window.document.querySelector('#date').focus(), 10);
  },
  trackVisibleRooms() {
    if (!visibilityTracker) {
      return;
    }

    const seen = visibilityTracker.getDisplayedRooms();
    const seenDuration = visibilityTracker.getDisplayedRoomsDurations();
    const clicked = visibilityTracker.getClickedRooms();
    const meta = visibilityTracker.getMetadata();

    visibilityTracker.clear();

    if (seen.length === 0 && clicked.length === 0) {
      return;
    }

    trackSearchVisit(seen, clicked);

    timeTrackingWatch.stop();
    const { startTime, duration, currentTime } = timeTrackingWatch.getTimes();

    App.track.custom(window.__DATA_TRACKING_SEEN_ROOMS, {
      seen: seen.join(', '),
      timeSeen: seenDuration.join(', '),
      clicked: clicked.join(', '),
      tagSlug: meta.tagSlug,
      hash: meta.hashId,
      timeVisited: startTime,
      timeExited: currentTime,
      landingLocationPage: Number(App.es6.shared.cookie.get('tgv_landing_location_page')) || null,
      duration,
    });
  },
  observeVisibleRooms({ state }) {
    if (!visibilityTracker) {
      return;
    }

    timeTrackingWatch = App.components.stop_watch();
    timeTrackingWatch.init();
    timeTrackingWatch.start();

    visibilityTracker.observe($('.v-room-carousel:not(.v-room-carousel--empty)'));
    visibilityTracker.setMetadata({
      tagSlug: state.eventTypeSlug,
      hashId: state.hashId,
      timestamp: Date.now(),
    });
  },
  observeVisibleRoomsAsync({ dispatch }) {
    setTimeout(() => dispatch('observeVisibleRooms'));
  },
  trackVisibleRoomsAddRoom(store, roomId) {
    if (!visibilityTracker) {
      return;
    }

    visibilityTracker.addRoomClicked(roomId);

    const seen = visibilityTracker.getDisplayedRooms();
    const clicked = visibilityTracker.getClickedRooms();

    trackSearchVisit(seen, clicked);
  },
  updateMetaTags({ state }) {
    App.components.meta_templates.update({
      eventType: state.eventType,
    });
  },
  clearHighlightedRoom({ commit }) {
    commit('clearHighlightedRoom');
  },
  switchEventToChristmas({ commit, dispatch, state, getters }) {
    let targetEvent = {
      key: 'christmas-party',
      value: 'Christmas Party',
    };

    if (state.l10n.ISOCountryCode === 'US') {
      targetEvent = {
        key: 'holiday-party',
        value: 'Holiday Party',
      };
    }

    if (getters.isChristmasDinner) {
      targetEvent = {
        key: 'christmas-dinner',
        value: 'Christmas Dinner',
      };
    }

    commit('updateEventType', targetEvent);

    dispatch('applyFilters', {
      changedFilter: 'Event',
      value: 'christmas-party',
    });

    App.track.all({ category: 's-search-page', action: 'search-christmas-party-instead' });
  },
  setTotalResultsVariable(store, resultsNumber) {
    window.__TGV_SEARCH_RESULTS_LENGTH = resultsNumber;
  },
  setRoomTagCookie({ state }) {
    setRoomTag(state.eventTypeSlug);
  },
  setFiltersCookie({ state, getters }) {
    const params = stateToQueryProps({ state, getters });
    params.eventCategory = state.eventCategory;

    setSearchFilters(params);
  },
  trackGA4PageView({ getters }) {
    App.track.pageViewEvent({ ...getters.ga4ParamsBase });
    App.track.event('search_page_pageview', { ...getters.ga4ParamsBase });
  },
  trackGA4Filters({ state, getters, dispatch }, { filter, filterValue }) {
    console.log('track', { filter, filterValue });
    // do not track initial automated filters
    if (
      filter === 'initMap'
      || filter === 'custom'
    ) {
      return;
    }

    if (filter === 'BackButton') {
      dispatch('trackGA4Interaction', {
        section: null,
        interaction: 'click',
        element: 'back_button',
        elementValue: null,
      });
      return;
    }

    if (filter === 'map') {
      dispatch('trackGA4Interaction', {
        section: 'map',
        interaction: 'change',
        element: 'map',
        elementValue: getters.mapCenter,
      });
      return;
    }

    if (filter === 'map-reset') {
      dispatch('trackGA4Interaction', {
        section: 'map',
        interaction: 'reset',
        element: 'map',
        elementValue: getters.mapCenter,
      });
      return;
    }

    if (filter === 'Page') {
      dispatch('trackGA4Interaction', {
        section: 'list',
        interaction: 'change',
        element: 'page',
        elementValue: filterValue,
      });
      return;
    }

    if (filter === 'try_guided_search_button') {
      const interaction = filterValue === 'click' ? 'click' : 'visible';

      dispatch('trackGA4Interaction', {
        ...getters.ga4Params,
        event: 'search_page_filters_engagement',
        section: state.mobileView === 'map' ? 'mobile_map_navigation' : 'search_listing_navigation',
        element: filter,
        element_value: null,
        interaction,
      });
      return;
    }

    if (['date_filter', 'people_filter', 'catering_filter', 'drinks_filter', 'layout_filter', 'area_type_filter'].includes(filter)) {
      dispatch('trackGA4Interaction', {
        ...getters.ga4Params,
        event: 'search_page_filters_engagement',
        section: 'filter_questions_flow',
        element: filter,
        elementValue: filterValue,
        interaction: 'click',
      });
      return;
    }

    if (filter.includes('filters_modal')) {
      const data = {
        event: 'search_page_filters_engagement',
        section: state.mobileView === 'map' ? 'mobile_map_navigation' : 'search_listing_navigation',
        element: filter,
        elementValue: filterValue,
      };
      if (filter === 'filters_modal_popup') {
        dispatch('trackGA4Interaction', {
          ...data,
          interaction: 'visible',
        });
      } else if (['filters_modal_location', 'filters_modal_people'].includes(filter)) {
        dispatch('trackGA4Interaction', {
          ...data,
          interaction: 'change',
        });
      } else {
        dispatch('trackGA4Interaction', {
          ...data,
          interaction: 'click',
        });
      }
      return;
    }

    const data = {
      ...getters.ga4Params,
      interaction: 'change',
      section: state.mobileView === 'map' ? 'mobile_map_navigation' : 'search_listing_navigation',
      element: GA4FiltersMap[filter] ?? filter,
      element_value: filterValue?.value ?? filterValue,
    };

    App.track.event('search_page_filters_engagement', data);
  },
  trackRoomClick({ getters, state }, { position, roomId, venueId, section, element, elementValue }) {
    const room = getters.roomsMap[roomId];
    const data = {
      ...getters.ga4ParamsBase,
      room_id: roomId,
      venue_id: venueId,
      section,
      element,
      position,
      element_value: elementValue,
      interaction: 'click',
      exp_response_time: room ? Math.round(room.response_time / 60) : null,
      exp_online_status: getOnlineLabelStatus(state?.managersLastActivityOnline[venueId], true),
      exp_space_venue_type_id: room?.space_venue_type_pair_id,
      exp_space_venue_type_label: room?.space_venue_type_label_variant,
      exp_area_type_slug: room?.venue_part,
      exp_area_type_label: room?.venue_part_short_label,
    };

    App.track.event('clicked_to_room_page', data);
  },
  trackGA4Interaction({ getters }, { section, interaction, element, elementValue, event = 'search_page_interaction' }) {
    const data = {
      ...getters.ga4Params,
      section,
      interaction,
      element,
      element_value: elementValue,
    };

    App.track.event(event, data);
  },
  updateTrackingCity({ commit, state }) {
    commit('setTrackingCity', state.rooms[0]?.city);
  },
  getManagersLastActivityOnline({ dispatch, state }) {
    const venueIds = new Set(state.rooms.map(room => room.venue_id));

    if (state.highlightedRoomId && state.highlightedRoomData?.venue_id) {
      venueIds.add(state.highlightedRoomData.venue_id);
    }

    dispatch('fetchManagersLastActivityOnline', [...venueIds]);
  },
  fetchManagersLastActivityOnline({ commit, dispatch }, venueIds) {
    if (!venueIds.length) {
      return;
    }

    // Cancel any pending timer from a previous call (timer leak fix)
    clearTimeout(searchActivityTimer);
    lastVenueIds = venueIds;

    // Serve cached venues immediately, only fetch stale ones (single localStorage read)
    const { cached: cachedEntries, staleIds } = getCachedActivities(venueIds);
    if (Object.keys(cachedEntries).length) {
      commit('updateManagersLastActivityOnline', cachedEntries);
    }

    const scheduleNext = () => {
      searchActivityTimer = setTimeout(() => {
        dispatch('fetchManagersLastActivityOnline', lastVenueIds);
      }, 15 * 60 * 1000); // every 15 minutes
    };

    // Register visibility listener once — pause polling when tab hidden, resume on return
    if (!visibilityListenerAdded) {
      visibilityListenerAdded = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          clearTimeout(searchActivityTimer);
        } else {
          dispatch('fetchManagersLastActivityOnline', lastVenueIds);
        }
      });
    }

    if (!staleIds.length) {
      scheduleNext(); // all cached — just reschedule
      return;
    }

    const endpoint = '/venue-data/last-manager-activity';
    $.ajax({
      url: endpoint,
      data: { ids: staleIds },
      dataType: 'json',
    })
      .done((data) => {
        setCachedActivities(data.lastManagerActivity);
        commit('updateManagersLastActivityOnline', data.lastManagerActivity);
        scheduleNext();
      });
  },
  ...roomActions,
};

export default actions;
